import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ParameterInfo } from '@src/core/hooks';
import { changeLocation, type AppDispatch, type RootState } from '@src/core/store';
import { openCartModal } from '@src/features/cart/store/cart';
import {
  addNextProductPackageToAdderState,
  adderResumeSelector,
  adderState,
  changeProductAddStep,
  initializeAdderState,
  createInitialProductSelectionState,
  loadDefaultTariffGroup,
  loadProduct,
  loadTariffGroupAttributes,
  mountDefaultTariffs,
  productPackageProductsSelector,
  resetSelectedProduct,
  resetSteps,
  setHighlightedProductAdder,
  updateAdderStateProductSelectionState,
  type ProductSelectionState,
} from '@src/stores/product';

export interface MultiProductBookingState {
  isOpen: boolean;
  isCompleted: boolean;
  widgetId: string;
  sourceProductId: string;
  productIds: string[];
  currentProductIndex: number;
  params: ParameterInfo;
}

interface MultiProductBookingSliceState {
  booking?: MultiProductBookingState;
  isAddingToCart: boolean;
}

const initialState: MultiProductBookingSliceState = {
  booking: undefined,
  isAddingToCart: false,
};

const multiProductBookingSlice = createSlice({
  name: 'multiProductBooking',
  initialState,
  reducers: {
    openMultiProductBookingModal: (state, { payload }: PayloadAction<MultiProductBookingState>) => {
      state.booking = payload;
    },
    closeMultiProductBookingModal: (state) => {
      state.booking = undefined;
      state.isAddingToCart = false;
    },
    setAddingToCart: (state, { payload }: PayloadAction<boolean>) => {
      state.isAddingToCart = payload;
    },
  },
});

export default multiProductBookingSlice.reducer;

export const { openMultiProductBookingModal, closeMultiProductBookingModal, setAddingToCart } =
  multiProductBookingSlice.actions;

export const isAddingToCartSelector = (state: RootState) => state.multiProductBooking.isAddingToCart;

export const startMultiProductBooking =
  (
    widgetId: string,
    sourceProductId: string,
    productIds: string[],
    params: ParameterInfo,
    autoSelectFirstProduct = false,
  ) =>
  (dispatch: AppDispatch) => {
    if (productIds.length === 0) return;

    const productSelectionState = productIds.reduce((acc, id) => {
      acc[id] = createInitialProductSelectionState();
      return acc;
    }, {} as Record<string, ProductSelectionState>);

    dispatch(
      openMultiProductBookingModal({
        isOpen: true,
        isCompleted: false,
        widgetId,
        sourceProductId,
        productIds,
        currentProductIndex: 0,
        params,
      }),
    );

    const firstProduct = autoSelectFirstProduct ? productIds[0]! : '';
    dispatch(initializeAdderState(widgetId, firstProduct, ''));
    dispatch(updateAdderStateProductSelectionState({ id: widgetId, value: productSelectionState }));

    if (firstProduct) {
      dispatch(mountDefaultTariffs(firstProduct));
      dispatch(loadProduct(firstProduct));
      dispatch(loadDefaultTariffGroup(firstProduct));
      dispatch(loadTariffGroupAttributes(firstProduct));
    }
  };

export const startPackageMultiProductBooking =
  (widgetId: string, packageId: string, params: ParameterInfo) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const products = productPackageProductsSelector(packageId)(getState());

    if (products?.length) {
      const productIds = products.map((p) => p.productId);
      dispatch(resetSteps(widgetId));
      dispatch(
        openMultiProductBookingModal({
          isOpen: true,
          isCompleted: false,
          widgetId,
          sourceProductId: packageId,
          productIds,
          currentProductIndex: 0,
          params,
        }),
      );
      return;
    }

    dispatch(setHighlightedProductAdder(widgetId, params));
  };

export const selectProductForBooking =
  (widgetId: string, productId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const booking = multiProductBookingSelector(getState());
    if (booking) {
      const newIndex = booking.productIds.indexOf(productId);
      if (newIndex >= 0 && newIndex !== booking.currentProductIndex) {
        dispatch(openMultiProductBookingModal({ ...booking, currentProductIndex: newIndex }));
      }
    }
    dispatch(resetSelectedProduct(widgetId, productId));
    dispatch(loadProduct(productId));
    dispatch(loadDefaultTariffGroup(productId));
    dispatch(loadTariffGroupAttributes(productId));
  };

type SelectNextProduct = (nextProductId: string) => Parameters<AppDispatch>[0];

export const goToNextProduct =
  (widgetId: string, selectNext: SelectNextProduct) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const booking = multiProductBookingSelector(state);

    if (!booking) return;

    dispatch(changeProductAddStep({ id: widgetId, value: 'finish' }));

    // Re-read after dispatch so the just-finished product already shows 'finish'
    const { productSelectionState } = adderState(widgetId)(getState());
    const nextIndex = booking.productIds.findIndex(
      (id) => productSelectionState[id]?.currentProductAdderStep !== 'finish',
    );

    if (nextIndex >= 0) {
      dispatch(openMultiProductBookingModal({ ...booking, currentProductIndex: nextIndex }));
      dispatch(selectNext(booking.productIds[nextIndex]!));
      return;
    }

    dispatch(openMultiProductBookingModal({ ...booking, isCompleted: true }));
  };

// Package flow only auto-loads the next product when autoSelectProducts is set — otherwise it
// resets to '' so the mobile product list shows first (see PR-4364 item 4).
export const advanceMultiProductBooking =
  (widgetId: string, isPackage: boolean) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { autoSelectProducts } = adderState(widgetId)(getState());

    const selectNext: SelectNextProduct = isPackage
      ? (nextId) =>
          autoSelectProducts ? addNextProductPackageToAdderState(widgetId, nextId) : resetSelectedProduct(widgetId, '')
      : (nextId) => selectProductForBooking(widgetId, nextId);

    dispatch(goToNextProduct(widgetId, selectNext));
  };

export const abandonMultiProductBooking = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const booking = multiProductBookingSelector(state);

  dispatch(closeMultiProductBookingModal());

  if (booking) {
    dispatch(initializeAdderState(booking.widgetId, booking.sourceProductId, ''));
  }

  const checkoutPage = booking?.params.values['checkout-page'];
  const autoRedirect = booking?.params.values['auto-checkout-redirect'];

  if (autoRedirect && checkoutPage && /^[/#]/.test(checkoutPage)) {
    dispatch(changeLocation(checkoutPage));
    return;
  }

  const openCartParam = booking?.params.values['open-cart-when-product-added'];
  const enableModal = booking?.params.values['enable-modal'];
  if (openCartParam === 'true' || enableModal === 'true') {
    dispatch(openCartModal());
  }
};

export const multiProductBookingSelector = (state: RootState) => state.multiProductBooking.booking;

export const isMultiProductBookingCompleted = (state: RootState) =>
  Boolean(state.multiProductBooking.booking?.isCompleted);

export const multiProductBookingTotalCentsSelector = (widgetId: string) => (state: RootState) => {
  const booking = state.multiProductBooking.booking;
  if (!booking) return 0;
  return booking.productIds.reduce(
    (sum, productId) => sum + (adderResumeSelector(widgetId, productId)(state).totalCents ?? 0),
    0,
  );
};
